<template>
  <div class="flex flex-col gap-2">
    <Toolbar v-show="editing">
      <template #center
        ><InputGroup
          ><Select
            class="w-96 rounded-r-none"
            v-model="selectedFlexRowTemplate"
            :options="flexRowTemplates"
            scroll-height="30rem"
            optionLabel="name"
            placeholder="Select a template"
            @change="loadFlexRows(selectedFlexRowTemplate?.fields)"
            ><template #option="slotProps">
              <div class="flex align-items-center">
                <div class="me-2">{{ slotProps.option.name }}</div>
                <i v-if="slotProps.option.defaultTemplate" class="pi pi-star"></i>
              </div>
            </template> </Select
          ><Button icon="pi pi-cog" @click="customizeFlexRows = true" /></InputGroup
      ></template>
    </Toolbar>
    <div class="flex flex-row flex-wrap gap-4 transform-gpu will-change-transform">
      <div v-for="(field, i) in fields" :key="field.id" class="flex flex-col w-fit gap-1">
        <FlexFieldInput v-model:field="fields[i]" :editing="editing"></FlexFieldInput>
      </div>
    </div>
    <div v-if="!editing && fields.length === 0" class="w-full text-center py-8">
      <p>No Additional Fields Have Been Added To This Project</p>
    </div>
  </div>
  <TSDialog label="Customize Flex Rows" v-model:visible="customizeFlexRows" v-if="customizeFlexRows" icon="cog" size="w-[900px]!">
    <template #content>
      <FlexRowTemplateManager modal :initial-rows="fields" @confirm="flexRowsCustomized"></FlexRowTemplateManager>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { getFlexRowTemplates } from "@/services/FlexRowsService";
import { type FlexRowField, type FlexRowTemplate } from "@/types/FlexRows";

const customizeFlexRows = defineModel<boolean>("customizeFlexRows", { required: true });
const allFields = defineModel<FlexRowField[]>("allFields", { required: true });
const flexRowTemplates = defineModel<FlexRowTemplate[]>("flexRowTemplates", { required: true });
const selectedFlexRowTemplate = defineModel<FlexRowTemplate | undefined>("selectedFlexRowTemplate", { required: true });
const fields = defineModel<FlexRowField[]>("fields", { required: true });
const loadDefaultTemplate = defineModel<boolean>("loadDefaultTemplate", { default: false });

interface Props {
  editing?: boolean;
  currentFlexFields?: FlexRowField[];
}
const props = withDefaults(defineProps<Props>(), {
  editing: true,
});

watch(
  () => selectedFlexRowTemplate.value,
  (template: FlexRowTemplate | undefined) => {
    if (template != null) loadFlexRows(template.fields);
  }
);

const setTemplate = (): void => {
  let previousTemplate = selectedFlexRowTemplate.value ? selectedFlexRowTemplate.value.settingId : null;

  for (let template of flexRowTemplates.value) {
    if (previousTemplate) {
      if (template.settingId === previousTemplate) {
        selectedFlexRowTemplate.value = template;
      }
    } else if (template.defaultTemplate) {
      selectedFlexRowTemplate.value = template;
    }
  }
};

const loadFlexRows = (includedFields: FlexRowField[] | undefined): void => {
  if (includedFields == null) return;

  const newFields: FlexRowField[] = [];
  for (let includedField of includedFields) {
    const field = allFields.value.find((f: FlexRowField) => f.customField === includedField.customField && f.id === includedField.id);
    if (field != null) {
      const existingField = fields.value.find((f: FlexRowField) => f.customField === field.customField && f.id === field.id);
      if (existingField != null) {
        field.textValue = existingField.textValue;
        field.numericValue = existingField.numericValue;
        field.dateTimeValue = existingField.dateTimeValue;
        field.booleanValue = existingField.booleanValue;
      } else {
        field.textValue = null;
        field.numericValue = null;
        field.dateTimeValue = null;
        field.booleanValue = null;
      }
      newFields.push(field);
    }
  }

  newFields.sort((a, b) => a.label.localeCompare(b.label));

  fields.value = newFields;
};

const flexRowsCustomized = async (customFields: FlexRowField[]): Promise<void> => {
  getFlexRowTemplates().then((result: FlexRowTemplate[]) => (flexRowTemplates.value = result));
  customizeFlexRows.value = false;
  loadFlexRows(customFields);
  selectedFlexRowTemplate.value = undefined;
};

onBeforeMount(async (): Promise<void> => {
  if (loadDefaultTemplate.value) {
    setTemplate();
    loadDefaultTemplate.value = false;
  }
});
</script>
